import { ThemeEvents } from '@theme/events';
import { convertMajorUnitsToMinorUnits, formatMoney } from '@theme/money-formatting';

const MONEY_FORMAT_NO_DECIMALS = {
  amount: 'amount_no_decimals',
  amount_with_comma_separator: 'amount_no_decimals_with_comma_separator',
  amount_with_space_separator: 'amount_no_decimals_with_space_separator',
  amount_with_period_and_space_separator: 'amount_no_decimals_with_period_and_space_separator',
  amount_with_apostrophe_separator: 'amount_no_decimals_with_apostrophe_separator',
};

const FREE_SHIPPING_PROGRESS_UPDATE_EVENT = 'free-shipping-progress:update';
const EXCLUDED_PRODUCT_TYPES = ['Promo'];

let pendingCartFetch = null;

class FreeShippingProgress extends HTMLElement {
  #cartUpdateRequestId = 0;

  connectedCallback() {
    if (!this.#update() && this.#hasCartItems()) {
      this.#scheduleInitialRenderRetries();
    }

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(FREE_SHIPPING_PROGRESS_UPDATE_EVENT, this.#handleProgressUpdate);

    if (this.#opensCartDrawer()) {
      this.addEventListener('click', this.#handleOpenCartDrawer);
      this.addEventListener('keydown', this.#handleOpenCartDrawerKeydown);
    }
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(FREE_SHIPPING_PROGRESS_UPDATE_EVENT, this.#handleProgressUpdate);

    if (this.#opensCartDrawer()) {
      this.removeEventListener('click', this.#handleOpenCartDrawer);
      this.removeEventListener('keydown', this.#handleOpenCartDrawerKeydown);
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #handleProgressUpdate = (event) => {
    const cart = event.detail?.cart;

    if (this.#isCartPayload(cart)) {
      this.#update(cart);
    }
  }

  /**
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    const requestId = ++this.#cartUpdateRequestId;
    const cart = event.detail?.resource;

    if (this.#isCartPayload(cart)) {
      this.#update(cart);
    }

    this.#fetchAndUpdate(requestId, this.#fetchCart(event));
  };

  #handleOpenCartDrawer = () => {
    const cartDrawer = document.querySelector('cart-drawer-component');

    if (typeof cartDrawer?.open === 'function') {
      cartDrawer.open();
    }
  };

  /**
   * @param {KeyboardEvent} event
   */
  #handleOpenCartDrawerKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    event.preventDefault();
    this.#handleOpenCartDrawer();
  };

  /**
   * @param {number} requestId
   * @param {Promise<unknown>} cartFetch
   */
  async #fetchAndUpdate(requestId, cartFetch) {
    try {
      const cart = await cartFetch;

      if (requestId !== this.#cartUpdateRequestId) return;

      this.#update(cart);
    } catch (error) {
      console.error('Failed to update free shipping progress:', error);
    }
  }

  /**
   * @param {CustomEvent} [event]
   * @returns {Promise<unknown>}
   */
  #fetchCart(event) {
    if (event?.detail) {
      event.detail.freeShippingProgressCartFetch = event.detail.freeShippingProgressCartFetch || this.#requestCart();

      return event.detail.freeShippingProgressCartFetch;
    }

    if (!pendingCartFetch) {
      pendingCartFetch = this.#requestCart().finally(() => {
        pendingCartFetch = null;
      });
    }

    return pendingCartFetch;
  }

  /**
   * @returns {Promise<unknown>}
   */
  #requestCart() {
    return fetch('/cart.js').then((response) => response.json());
  }

  #scheduleInitialRenderRetries() {
    requestAnimationFrame(() => {
      if (this.#update()) return;

      setTimeout(() => {
        if (this.#update()) return;
      }, 100);

      setTimeout(() => {
        if (this.#update()) return;

        if (this.#hasCartItems()) {
          const requestId = ++this.#cartUpdateRequestId;
          this.#fetchAndUpdate(requestId, this.#fetchCart());
        }
      }, 300);
    });
  }

  /**
   * @param {unknown} cart
   * @returns {cart is { total_price?: number, item_count?: number, items?: Array<unknown> }}
   */
  #isCartPayload(cart) {
    return (
      typeof cart === 'object' &&
      cart !== null &&
      'total_price' in cart &&
      ('item_count' in cart || 'items' in cart)
    );
  }

  #hasCartItems() {
    return Number(this.dataset.cartItemCount ?? 0) > 0;
  }

  #opensCartDrawer() {
    return this.dataset.opensCartDrawer === 'true';
  }

  /**
   * @param {unknown} item
   * @returns {boolean}
   */
  #isExcludedCartItem(item) {
    if (typeof item !== 'object' || item === null) return false;

    const cartItem = /** @type {{ product_type?: unknown }} */ (item);

    return EXCLUDED_PRODUCT_TYPES.includes(String(cartItem.product_type || ''));
  }

  /**
   * @param {{ total_price?: number, items?: Array<unknown> }} [cart]
   * @returns {number}
   */
  #freeShippingCartTotal(cart) {
    if (!Array.isArray(cart?.items)) {
      return Number(cart?.total_price ?? this.dataset.cartTotal ?? 0);
    }

    return cart.items.reduce((total, item) => {
      if (this.#isExcludedCartItem(item)) return total;

      const cartItem = /** @type {{ final_line_price?: unknown, line_price?: unknown }} */ (item);

      return total + Number(cartItem.final_line_price ?? cartItem.line_price ?? 0);
    }, 0);
  }

  /**
   * @param {{ total_price?: number, item_count?: number, items?: Array<unknown> }} [cart]
   * @returns {boolean}
   */
  #update(cart) {
    const cartTotal = this.#freeShippingCartTotal(cart);
    const itemCount = Number(
      cart?.item_count ??
        cart?.items?.reduce((total, item) => total + (Number(item?.quantity) || 0), 0) ??
        this.dataset.cartItemCount ??
        0
    );
    const baseAmount = Number.parseFloat(this.dataset.baseAmount || '');
    const activeCurrency = window.Shopify?.currency?.active || '';
    const rate = Number.parseFloat(window.Shopify?.currency?.rate || '1');
    const rawMoneyFormat = this.dataset.moneyFormat || '';

    if (!activeCurrency || Number.isNaN(rate) || Number.isNaN(baseAmount) || !rawMoneyFormat) return false;

    this.#setData('cartTotal', cartTotal.toString());
    this.#setData('cartItemCount', itemCount.toString());

    if (itemCount <= 0) {
      this.hidden = true;
      this.#setData('ready', 'false');
      return true;
    }

    const threshold = this.#threshold();
    const remaining = Math.max(threshold - cartTotal, 0);
    const unlocked = remaining <= 0;
    const progress = threshold > 0 ? Math.min((cartTotal / threshold) * 100, 100) : 100;
    const message = this.querySelector('[data-progress-message]');
    const progressBar = this.querySelector('[data-progress-bar]');

    if (!(message instanceof HTMLElement) || !(progressBar instanceof HTMLElement)) return false;

    this.#setData('freeShippingUnlocked', unlocked ? 'true' : 'false');

    progressBar.style.setProperty('--free-shipping-progress', `${progress}%`);

    if (message instanceof HTMLElement) {
      message.textContent =
        unlocked
          ? "🎉 You've unlocked free shipping ✅"
          : `Only ${this.#formatAmount(remaining, activeCurrency, rawMoneyFormat)} away from free shipping 🎁`;
      this.#setData('longMessage', message.textContent.length > 54 ? 'true' : 'false');
    }

    this.hidden = false;
    this.#setData('ready', 'true');

    return true;
  }

  /**
   * @param {string} key
   * @param {string} value
   */
  #setData(key, value) {
    if (this.dataset[key] !== value) {
      this.dataset[key] = value;
    }
  }

  /**
   * @returns {number}
   */
  #threshold() {
    const baseAmount = Number.parseFloat(this.dataset.baseAmount || '');
    const activeCurrency = window.Shopify?.currency?.active || '';
    const rate = Number.parseFloat(window.Shopify?.currency?.rate || '1');

    if (!activeCurrency || Number.isNaN(rate) || Number.isNaN(baseAmount)) return 0;

    return convertMajorUnitsToMinorUnits(Math.ceil(baseAmount * rate), activeCurrency);
  }

  /**
   * @param {number} amount
   * @param {string} currency
   * @param {string} rawMoneyFormat
   * @returns {string}
   */
  #formatAmount(amount, currency, rawMoneyFormat) {
    const baseCurrency = this.dataset.baseCurrency || '';
    const currencyFormat = baseCurrency ? rawMoneyFormat.replace(baseCurrency, '{{currency}}') : rawMoneyFormat;
    const moneyFormat = this.#formatWithoutDecimals(currencyFormat);
    const majorAmount = Math.ceil(this.#minorUnitsToMajorUnits(amount, currency));
    const roundedAmount = convertMajorUnitsToMinorUnits(majorAmount, currency);

    return formatMoney(roundedAmount, moneyFormat, currency);
  }

  /**
   * @param {number} amount
   * @param {string} currency
   * @returns {number}
   */
  #minorUnitsToMajorUnits(amount, currency) {
    const oneMajorUnit = convertMajorUnitsToMinorUnits(1, currency);
    return amount / oneMajorUnit;
  }

  /**
   * @param {string} format
   * @returns {string}
   */
  #formatWithoutDecimals(format) {
    return format.replace(/{{\s*(\w+)\s*}}/g, (match, placeholder) => {
      if (typeof placeholder !== 'string') return match;

      return `{{${MONEY_FORMAT_NO_DECIMALS[placeholder] || placeholder}}}`;
    });
  }
}

if (!customElements.get('free-shipping-progress')) {
  customElements.define('free-shipping-progress', FreeShippingProgress);
}
